import type { ComponentProps, PropsWithChildren } from "react";
import * as heroIcons from "@heroicons/react/24/outline";
import { tv, type VariantProps } from "tailwind-variants";

import type { SVGProps } from "@/shared.types";

const iconsVariants = tv({
  slots: {
    wrapper: "flex shrink-0 flex-row items-center justify-center",
    icon: "animate-spin text-current",
  },
  variants: {
    /*
      The variant sizes the wrapper and the icon inside it; sizing the wrapper
      alone leaves the icon unsized.
    */
    size: {
      sm: { wrapper: "size-5", icon: "size-5" },
      md: { wrapper: "size-6", icon: "size-6" },
      lg: { wrapper: "size-7", icon: "size-7" },
      xl: { wrapper: "size-10", icon: "size-10" },
    },
  },
});

const { icon, wrapper } = iconsVariants();

export const icons = {
  ...heroIcons,

  SpinnerIcon: ({ className, ...props }: SVGProps) => {
    return (
      <svg
        aria-hidden="true"
        className={icon({ className, size: className ? undefined : "lg" })}
        fill="none"
        focusable="false"
        role="presentation"
        viewBox="0 0 100 101"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
      >
        <path
          className="opacity-25"
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentColor"
          opacity="0.9"
        />
      </svg>
    );
  },

  EoCareIcon: ({ className, ...props }: SVGProps) => {
    return (
      <svg
        aria-hidden="true"
        className={className}
        fill="currentColor"
        focusable="false"
        role="presentation"
        viewBox="0 0 95 46"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
      >
        <path
          d="M13.5795 26.8806C13.8304 29.3893 15.0193 31.1898 16.8278 32.6061C21.5259 36.2825 28.4614 35.7351 32.5601 31.3882C34.4689 29.3617 34.7298 29.319 37.1051 30.8357C38.8309 31.9382 40.5416 33.0632 42.2422 34.2032C43.6494 35.1475 43.7873 35.936 42.7589 37.3322C39.1469 42.229 34.1528 44.7703 28.2608 45.6367C22.908 46.4227 17.643 46.0008 12.679 43.7031C4.47931 39.9112 0.433375 33.3319 0.0320419 24.3744C-0.231333 18.4807 1.10811 13.059 4.82295 8.39823C9.25266 2.83343 15.2225 0.297126 22.1981 0.0234057C28.4765 -0.222691 34.1278 1.44976 38.6076 6.09045C42.4002 10.0154 44.1485 14.8721 44.6653 20.2209C44.7932 21.5519 44.8258 22.8953 44.8358 24.2338C44.8509 26.2177 44.1811 26.8781 42.2121 26.8806C33.1821 26.8856 24.1546 26.8806 15.1247 26.8806C14.6355 26.8806 14.1464 26.8806 13.5795 26.8806ZM31.5693 17.9709C31.9606 15.181 30.1771 11.9566 27.5961 10.6884C23.6279 8.73724 18.0193 10.0029 15.3153 13.4884C14.3095 14.7867 13.6372 16.2332 13.6146 17.9709H31.5693Z"
          fill="currentColor"
        />
        <path
          d="M94.8591 22.9307C94.8591 36.2576 85.1543 45.9558 71.815 45.9583C58.4908 45.9608 48.7183 36.1295 48.756 22.7549C48.7911 9.42801 58.3779 -0.0140726 71.8652 0.000994569C85.9971 0.0160617 95.3005 10.4702 94.8591 22.9307ZM72.0558 14.5182C71.8702 14.3926 71.7298 14.3123 71.6069 14.2093C69.5074 12.4389 67.132 11.9668 64.5911 12.9688C61.9573 14.0059 60.4774 16.0651 60.1638 18.8651C59.8227 21.9036 60.7232 24.6584 62.4464 27.1369C64.7115 30.3889 67.8519 32.5486 71.4288 34.1181C71.7874 34.2763 72.3443 34.2436 72.713 34.0804C75.6127 32.8122 78.2088 31.0921 80.3459 28.7265C82.4855 26.3534 83.7472 23.6037 83.9503 20.3843C84.1786 16.7557 82.0089 13.5288 78.6452 12.6951C76.1494 12.0773 73.9496 12.7729 72.0583 14.5207L72.0558 14.5182Z"
          fill="currentColor"
        />
      </svg>
    );
  },
};

type IconWrapperProps = Pick<ComponentProps<"div">, "className" | "style"> &
  VariantProps<typeof iconsVariants>;

/*
  No aria-hidden here: the slot also carries interactive content — the reveal
  toggle in PasswordInput — and a focusable control inside aria-hidden fails
  WCAG 4.1.2. Every icon from the library already hides itself, so decorative icons
  are unaffected.
*/
export const IconWrapper = ({
  children,
  className,
  size = "md",
  style,
}: PropsWithChildren<IconWrapperProps>) => {
  return (
    <div className={wrapper({ className, size })} style={style}>
      {children}
    </div>
  );
};
